import React, { useState, useEffect, useMemo } from "react";
import { Container, Header, Button, Dropdown, Input } from "semantic-ui-react";
import { getHostList } from "services/developer";
import { ProfileTable } from "components/Admin/Profiles/ProfileTable";
import { PAGE_SIZE } from "env.config";
import { ProfileFormModal } from "components/Admin/Profiles/ProfileFormModal";
import { DeleteProfileModal } from "components/Admin/Profiles/DeleteProfileModal";
import { withRouter } from "react-router-dom";
import { handleMessage } from "utils/function-utils";
import { PortalMessage } from "components/Admin/PortalMessage";
import {
  getProfileList,
  getMES16HostList,
  addProfile,
  upateProfile,
  deleteProfile,
} from "services/developer";
const TableActions = ({
  setOpenDeleteModal,
  selectedItem,
  hostFilterValue,
  handleFormModal,
  history,
}) => {
  return (
    <Button.Group style={{ float: "right" }}>
      <Button
        content="Add"
        disabled={!hostFilterValue}
        onClick={() => handleFormModal("add")}
      />
      <Button
        content="Edit"
        disabled={!selectedItem || !selectedItem.ProfileId}
        onClick={() => handleFormModal("edit")}
      />
      <Button
        content="Delete"
        disabled={!selectedItem || !selectedItem.ProfileId}
        onClick={() => setOpenDeleteModal(true)}
      />
      <Button
        content="Define Permissions"
        onClick={() =>
          history.push(
            `/admin/profiles/permissions?id=${selectedItem.ProfileId}`
          )
        }
        disabled={!selectedItem || !selectedItem.ProfileId}
      />
    </Button.Group>
  );
};

const TableActionsWithRouter = withRouter(TableActions);

const Profile = () => {
  const [hostList, setHostList] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [renderList, setRenderList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showSuccessMes, setShowSuccessMes] = useState("");
  const [showFailMes, setShowFailMes] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [activePage, setActivePage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [formData, setFormData] = useState({});
  const [filter, setFilter] = useState("");
  const [hostFilterValue, setHostFilterValue] = useState("");
  useEffect(() => {
    (async () => {
      const [MES15HostList, MES16HostList] = await Promise.all([
        getHostList(),
        getMES16HostList(),
      ]);
      const hostList = MES15HostList.concat(MES16HostList);

      let host = hostList[0].Host;
      setHostList(hostList);
      setHostFilterValue(host);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoaded(false);
    const delayDebounce = setTimeout(async () => {
      try {
        const profileList = await getProfileListWithFilters(
          { host: hostFilterValue, filter },
          hostList
        );
        const { renderList, totalPages } = onFilterProfileList(
          profileList,
          hostFilterValue
        );
        setHostFilterValue(hostFilterValue);
        setRenderList(renderList);
        setTotalPages(totalPages);
      } catch (error) {
        console.error("Failed to fetch profile list:", error);
      }
    }, 500); // <-- debounce delay

    return () => clearTimeout(delayDebounce);
  }, [hostFilterValue, filter, hostList]);

  const searchHostOptions = useMemo(() => {
    return hostList.map((host) => {
      return {
        key: host.Host,
        text: (
          <span className="searchOption">
            {host.Host}
            <br />
            <i>{host.Name}</i>
          </span>
        ),
        value: host.Host,
        name: host.Name,
      };
    });
  }, [hostList]);

  const getProfileListWithFilters = async (params, hostList) => {
    if (!params.host || !hostList || !hostList.length) {
      return [];
    }
    let Scope, Stage, MES;
    const hostItem = hostList.find((item) => item.Host === params.host);
    Scope = hostItem?.Scope;
    Stage = hostItem?.Stage;
    MES = hostItem?.MES;

    params = { ...params, Scope, Stage, MES };
    const profileList = await getProfileList(params);
    const formatedProfileList = formatProfileList(profileList, hostList);
    setProfileList(formatedProfileList);
    setLoaded(true);
    return formatedProfileList;
  };

  const formatProfileList = (profileList, hostList) => {
    if (!profileList) return;
    let result = profileList.map((profile) => {
      if (!profile.MES || profile.MES !== "MES16") {
        profile.MES = "MES15";
        const currentHost = hostList.find(
          (host) => host.Scope === profile.Scope && host.Stage === profile.Stage
        );
        profile.Host = currentHost && currentHost.Host;
      }
      return profile;
    });
    sortColumnsByDefault(result);
    return result;
  };

  const onChangeFilter = (_e, { value }) => {
    setFilter(value);
    setActivePage(0);
    setSelectedItem({});
  };

  const onChangePage = (_e, { activePage }) => {
    const { renderList, totalPages } = onFilterProfileList(
      profileList,
      hostFilterValue,
      filter,
      activePage - 1
    );
    setRenderList(renderList);
    setTotalPages(totalPages);
    setActivePage(activePage - 1);
  };

  const handleFormModal = (modalStatus) => {
    const selectedHost = hostList.filter(
      (host) => host.Host === hostFilterValue
    )[0];
    const editStatus = modalStatus === "edit" ? true : false;
    setEditStatus(editStatus);
    initFormData(editStatus, selectedItem, selectedHost);
    setOpenFormModal(true);
  };

  const onSelectItem = (data) => {
    setSelectedItem(data);
  };

  const onChangeFormItem = (formKey, value) => {
    if (formKey === "name") {
      setFormData({ ...formData, name: value, nameValid: true });
    } else {
      setFormData({ ...formData, description: value });
    }
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  const handleSaveProfile = async () => {
    if (editStatus) {
      onEditProfile(formData);
    } else {
      onAddProfile(formData);
    }
  };

  const onAddProfile = async (formData) => {
    if (!formData.name.trim()) {
      setFormData({ ...formData, nameValid: false });
      return;
    }
    setOpenFormModal(false);
    setLoaded(false);
    const { scope, stage, name, description, MES, host } = formData;
    const params = {
      ProfileId: Date.now().toString(),
      Scope: scope,
      Stage: stage,
      ProfileName: name,
      Description: description,
      CreateDate: new Date().toISOString(),
      Permission: [],
      Host: host,
      MES,
    };
    const res = await addProfile(params);
    if (res.success) {
      const profileList = await getProfileListWithFilters(
        { host: hostFilterValue },
        hostList
      );
      const { renderList, totalPages, selectedItem, activePage } =
        onFilterListWithSelectedProfile(profileList, host, params.ProfileId);
      setFilter("");
      setProfileList(profileList);
      setHostFilterValue(host);
      setRenderList(renderList);
      setTotalPages(totalPages);
      setSelectedItem(selectedItem);
      setActivePage(activePage);
    }
    setLoaded(true);
    handleMessage({
      success: res.success,
      operation: "addProfile",
      content: res.message,
      setShowSuccessMes,
      setShowFailMes,
    });
  };

  const onEditProfile = async (formData) => {
    if (!formData.name.trim()) {
      setFormData({ ...formData, nameValid: false });
      return;
    }
    setOpenFormModal(false);
    setLoaded(false);
    const { scope, stage, name, description, MES, host } = formData;
    const params = {
      ProfileId: selectedItem.ProfileId,
      Scope: scope,
      Stage: stage,
      ProfileName: name,
      Description: description,
      MES,
      Host: host,
    };
    const res = await upateProfile(params);
    if (res.success) {
      const profileList = await getProfileListWithFilters(
        { host: hostFilterValue },
        hostList
      );
      const { renderList, totalPages, selectedItem, activePage } =
        onFilterListWithSelectedProfile(profileList, host, params.ProfileId);
      setFilter("");
      setProfileList(profileList);
      setHostFilterValue(host);
      setRenderList(renderList);
      setTotalPages(totalPages);
      setSelectedItem(selectedItem);
      setActivePage(activePage);
    }
    setLoaded(true);
    handleMessage({
      success: res.success,
      operation: "updateProfile",
      content: res.message,
      setShowSuccessMes,
      setShowFailMes,
    });
  };

  const onDeleteProfile = async () => {
    setOpenDeleteModal(false);
    setLoaded(false);
    const res = await deleteProfile({
      ProfileId: selectedItem.ProfileId,
      MES: selectedItem.MES,
    });
    if (res.success) {
      const profileList = await getProfileListWithFilters(
        { host: hostFilterValue },
        hostList
      );
      setProfileList(profileList);
      const { renderList, totalPages } = onFilterProfileList(
        profileList,
        selectedItem.Host
      );
      setFilter("");
      setHostFilterValue(selectedItem.Host);
      setRenderList(renderList);
      setTotalPages(totalPages);
      setActivePage(0);
      setSelectedItem({});
    }
    setLoaded(true);
    handleMessage({
      success: res.success,
      operation: "deleteProfile",
      content: res.message,
      setShowSuccessMes,
      setShowFailMes,
    });
  };

  const sortColumnsByDefault = (profileList) => {
    profileList.sort((a, b) => new Date(b.CreateDate) - new Date(a.CreateDate));
  };

  const initFormData = (editStatus, selectedItem, selectedHost) => {
    if (editStatus) {
      setFormData({
        MES: selectedItem.MES,
        scope: selectedItem.Scope,
        stage: selectedItem.Stage,
        host: selectedItem.Host,
        name: selectedItem.ProfileName,
        nameValid: true,
        description: selectedItem.Description,
      });
    } else {
      setFormData({
        MES: selectedHost.MES,
        scope: selectedHost.Scope,
        stage: selectedHost.Stage,
        host: selectedHost.Host,
        name: "",
        nameValid: true,
        description: "",
      });
    }
  };

  const onChangeHostSearch = async (value) => {
    setHostFilterValue(value);
    setActivePage(0);
    setSelectedItem({});
  };

  const onSearchChange = (options, filter) => {
    const list = options.filter(
      (item) =>
        item.value.toLowerCase().indexOf(filter.toLowerCase().trim()) > -1 ||
        item.name.toLowerCase().indexOf(filter.toLowerCase().trim()) > -1
    );
    return list;
  };

  const onFilterProfileList = (
    profileList,
    host,
    filter = "",
    activePage = 0
  ) => {
    if (!profileList) return;
    let filteredList = profileList
      .filter((item) => item?.Host === host)
      .filter(
        (item) =>
          item.ProfileName.toLowerCase().indexOf(filter.toLowerCase().trim()) >
            -1 ||
          item.Description.toLowerCase().indexOf(filter.toLowerCase().trim()) >
            -1
      );
    const renderList = filteredList.slice(
      activePage * PAGE_SIZE,
      (activePage + 1) * PAGE_SIZE
    );
    let totalPages = Math.ceil(filteredList.length / PAGE_SIZE);
    return { renderList, totalPages };
  };

  const onFilterListWithSelectedProfile = (profileList, host, profileId) => {
    const filteredList = profileList.filter((item) => item?.Host === host);
    const index = filteredList.findIndex(
      (profile) => profile.ProfileId === profileId
    );
    const selectedItem = filteredList[index] || {};
    const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);
    const activePage =
      Math.floor(index / PAGE_SIZE) === -1 ? 0 : Math.floor(index / PAGE_SIZE);
    const renderList = filteredList.slice(
      activePage * PAGE_SIZE,
      (activePage + 1) * PAGE_SIZE
    );
    return { renderList, totalPages, selectedItem, activePage };
  };

  return (
    <Container fluid style={{ padding: "2em" }}>
      <Header as="h1">Profiles</Header>
      <PortalMessage
        showSuccessMes={showSuccessMes}
        showFailMes={showFailMes}
        setShowSuccessMes={setShowSuccessMes}
        setShowFailMes={setShowFailMes}
      />
      <div id="host-dropdown">
        <Dropdown
          disabled={!loaded}
          loading={!loaded}
          search={onSearchChange}
          value={hostFilterValue}
          onChange={(_e, { value }) => onChangeHostSearch(value)}
          options={searchHostOptions}
          placeholder="Please select Host first"
          className="hostSearchDropdown"
          selection
        ></Dropdown>
      </div>
      <Input
        style={{ width: "300px" }}
        value={filter}
        icon="search"
        onChange={onChangeFilter}
        placeholder="Search by Profile Name or Description"
      />
      <TableActionsWithRouter
        handleFormModal={handleFormModal}
        setOpenDeleteModal={setOpenDeleteModal}
        selectedItem={selectedItem}
        hostFilterValue={hostFilterValue}
      />
      {hostFilterValue && (
        <ProfileTable
          renderList={renderList}
          loaded={loaded}
          activePage={activePage}
          totalPages={totalPages}
          selectedItem={selectedItem}
          onSelectItem={onSelectItem}
          onChangePage={onChangePage}
        />
      )}
      <ProfileFormModal
        editStatus={editStatus}
        openFormModal={openFormModal}
        formData={formData}
        setFormData={setFormData}
        onChangeFormItem={onChangeFormItem}
        onCloseFormModal={onCloseFormModal}
        onSaveProfile={handleSaveProfile}
      />
      <DeleteProfileModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        selectedItem={selectedItem}
        handleDeleteProfile={onDeleteProfile}
      />
    </Container>
  );
};

export default Profile;
